import type { CatalogRatioGroupData, Maybe } from '@gql/graphql';
import { DismantlingCoefficientTypeEnum, RatioTypeEnum, SortEnumType } from '@gql/graphql';

import type { RowClassRules } from 'ag-grid-community';

import type { TDefineActiveRatioGroupProps, TGetIsRatioGroupItemDisabled, TOrderState } from './types';

export const getRowClass: RowClassRules = {
    groupRow: ({ data }: { data: { groupRow: boolean } }) => data?.groupRow,
};

export const getRowId = ({ data }: { data: any }) => data?.id;

export const getWhereSearch = (searchStr: string) =>
    searchStr && {
        or: [
            {
                code: {
                    containsi: searchStr,
                },
            },
            {
                name: {
                    containsi: searchStr,
                },
            },
        ],
    };

export const getOrderValues = (
    sortType: Maybe<'asc' | 'desc'> | undefined,
    colId: string | undefined,
    prevState: TOrderState | TOrderState[] | undefined,
): TOrderState | TOrderState[] | undefined => {
    if (prevState && !colId) {
        return undefined;
    }

    const getColId = (id: string) => (id === 'number' ? 'fullNumber' : id);

    if (colId) {
        return [
            {
                [getColId(colId)]: sortType === 'asc' ? SortEnumType.Asc : SortEnumType.Desc,
            },
        ];
    }

    return prevState;
};

export const defineActiveRatioGroup = ({ checkedIds, activeTabId, groupsItems }: TDefineActiveRatioGroupProps) => {
    if (activeTabId && activeTabId !== RatioTypeEnum.TechPartCoef) {
        const activeRatioGroup = groupsItems.find(
            (group) => Object.values(checkedIds[activeTabId])?.[0]?.catalogRatioGroupId === group.id,
        );

        if (activeTabId === RatioTypeEnum.ConstraintCoef) {
            return activeRatioGroup ?? null;
        }

        if (activeTabId === RatioTypeEnum.DismantlingCoef) {
            if (activeRatioGroup?.groupType === DismantlingCoefficientTypeEnum.EquipmentInstallation) {
                return activeRatioGroup;
            }
            if (activeRatioGroup?.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks) {
                if (
                    Object.values(checkedIds[activeTabId])?.some(
                        (item) => item?.catalogRatioGroupId !== activeRatioGroup?.id,
                    )
                ) {
                    const constructionWorksGroup = groupsItems.find(
                        (group) =>
                            group.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks && !group.parentId,
                    );

                    return constructionWorksGroup ?? null;
                }

                return activeRatioGroup;
            }
        }
    }

    return null;
};

export const getIsRatioGroupItemDisabled =
    ({ ratioGroup, checkedRatiosByTypeLength }: TGetIsRatioGroupItemDisabled) =>
    (group?: CatalogRatioGroupData) => {
        if (!checkedRatiosByTypeLength) {
            return false;
        }

        if (group?.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks) {
            if (ratioGroup?.groupType === DismantlingCoefficientTypeEnum.EquipmentInstallation) {
                return true;
            }

            return group.parentId
                ? group.groupType !== DismantlingCoefficientTypeEnum.ConstructionWorks
                : ratioGroup?.id !== group.id && group.groupType !== DismantlingCoefficientTypeEnum.ConstructionWorks;
        }

        return ratioGroup?.id !== group?.id;
    };
